import { useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Mail,
  MessageSquare,
  NotebookPen,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  StickyNote,
  Trash2,
  Users as UsersIcon,
  Voicemail,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { Badge } from '@/components/badge';
import { IconButton } from '@/components/icon-button';
import { recordingUrl } from '@/lib/calls';
import { SentEmailDialog } from '@/components/sent-email-dialog';
import { formatDateTime, formatDuration, formatPhone } from '@/lib/format';
import type { ActivityItemData } from '@/types/api';

type BadgeTone = 'success' | 'warning' | 'neutral' | 'danger' | 'info';

/**
 * One rendered row. Feed entries from the API and locally derived ones (lead
 * stage changes, for instance) both reduce to this so a single list can hold
 * them in time order.
 */
export type FeedItem = {
  id: string;
  at: string;
  icon: ComponentType<LucideProps>;
  iconTone: string;
  title: string;
  body?: string | null;
  badge?: { label: string; tone: BadgeTone } | null;
  meta?: string[];
  /** Set for voicemails: renders an inline player for this call's recording. */
  recordingCallId?: number | null;
  /** Set for sent emails: opens the copy that went out. */
  mailDispatchId?: number | null;
  /** Where the title points, when the entry belongs to someone else's record. */
  href?: string | null;
  /**
   * Set only for entries this feed owns, meaning a note someone logged by hand.
   * Calls, texts and web inquiries are records of something that happened and
   * are deleted from the inbox that owns them, not from here.
   */
  onDelete?: (() => void) | null;
};

const KIND_ICON: Record<string, ComponentType<LucideProps>> = {
  sms: MessageSquare,
  email: Mail,
  note: StickyNote,
  meeting: UsersIcon,
  custom: NotebookPen,
  web_form: Globe,
};

const KIND_LABEL: Record<string, string> = {
  call: 'Call',
  voicemail: 'Voicemail',
  sms: 'Text',
  email: 'Email',
  note: 'Note',
  meeting: 'Meeting',
  custom: 'Note',
  web_form: 'Web',
};

function iconFor(item: ActivityItemData): ComponentType<LucideProps> {
  if (item.kind === 'voicemail') return Voicemail;
  if (item.kind === 'call') {
    if (item.direction === 'outbound') return PhoneOutgoing;
    return item.meta.status === 'completed' ? PhoneIncoming : PhoneMissed;
  }
  return KIND_ICON[item.kind] ?? NotebookPen;
}

function toneFor(item: ActivityItemData): string {
  if (item.kind === 'voicemail') return 'text-violet-600 dark:text-violet-400';
  if (item.kind === 'call') {
    return item.direction === 'outbound'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-blue-600 dark:text-blue-400';
  }
  return 'text-muted-foreground';
}

/** Turn one API entry into a row. */
export function toFeedItem(
  item: ActivityItemData,
  options: { showContactName?: boolean; onDelete?: (activityId: number) => void } = {},
): FeedItem {
  const meta: string[] = [];

  const party = typeof item.meta.party_number === 'string' ? item.meta.party_number : null;
  if (party) meta.push(formatPhone(party));

  const duration =
    typeof item.meta.duration_seconds === 'number' ? item.meta.duration_seconds : null;
  if (duration) meta.push(formatDuration(duration));

  for (const key of ['business', 'website', 'inquiry_kind', 'timeline'] as const) {
    const value = item.meta[key];
    if (typeof value === 'string' && value !== '') meta.push(value);
  }

  if (item.actor_name) {
    const verb = item.source_type === 'activity' ? 'Logged' : 'Sent';
    meta.push(`${verb} by ${item.actor_name}`);
  }
  if (options.showContactName && item.contact_name) meta.push(item.contact_name);

  return {
    id: item.id,
    at: item.occurred_at,
    icon: iconFor(item),
    iconTone: toneFor(item),
    title: item.title,
    body: item.body,
    badge: { label: KIND_LABEL[item.kind] ?? item.kind, tone: 'neutral' },
    meta,
    recordingCallId: item.has_recording && item.source_type === 'call' ? item.source_id : null,
    mailDispatchId: item.source_type === 'mail' ? item.source_id : null,
    href: options.showContactName ? `/contacts/${item.contact_id}` : null,
    onDelete:
      item.source_type === 'activity' && item.source_id != null && options.onDelete
        ? () => options.onDelete?.(item.source_id as number)
        : null,
  };
}

export function ActivityFeed({ items }: { items: FeedItem[] }): ReactNode {
  return (
    <ol className="relative space-y-5 border-l border-border/70 pl-5">
      {items.map((item) => (
        <FeedRow key={item.id} item={item} />
      ))}
    </ol>
  );
}

function FeedRow({ item }: { item: FeedItem }) {
  const [playing, setPlaying] = useState(false);
  const [viewingEmail, setViewingEmail] = useState(false);
  const Icon = item.icon;

  return (
    <li className="group/feed-row relative">
      <span
        className="absolute -left-[26px] inline-flex size-5 items-center justify-center rounded-full bg-card ring-2 ring-border/70"
        aria-hidden
      >
        <Icon className={`size-3 ${item.iconTone}`} />
      </span>
      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline gap-2 text-sm">
          {item.href ? (
            <Link to={item.href} className="font-medium hover:text-primary hover:underline">
              {item.title}
            </Link>
          ) : (
            <span className="font-medium">{item.title}</span>
          )}
          {item.badge ? <Badge tone={item.badge.tone}>{item.badge.label}</Badge> : null}
          <span className="text-xs text-muted-foreground">{formatDateTime(item.at)}</span>
          {item.recordingCallId ? (
            <button
              type="button"
              onClick={() => setPlaying((v) => !v)}
              className="rounded-md px-1.5 py-0.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
            >
              {playing ? 'Hide' : 'Listen'}
            </button>
          ) : null}
          {item.mailDispatchId ? (
            <button
              type="button"
              onClick={() => setViewingEmail(true)}
              className="rounded-md px-1.5 py-0.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
            >
              View email
            </button>
          ) : null}
          {item.onDelete ? (
            <IconButton
              type="button"
              aria-label="Delete note"
              onClick={item.onDelete}
              className="size-6 opacity-0 transition-opacity focus:opacity-100 group-hover/feed-row:opacity-100"
            >
              <Trash2 className="size-3.5" />
            </IconButton>
          ) : null}
        </div>

        {viewingEmail && item.mailDispatchId ? (
          <SentEmailDialog dispatchId={item.mailDispatchId} onClose={() => setViewingEmail(false)} />
        ) : null}

        {playing && item.recordingCallId ? (
          <audio
            controls
            preload="none"
            src={recordingUrl(item.recordingCallId)}
            className="w-full max-w-md"
          >
            Your browser does not support audio playback.
          </audio>
        ) : null}

        {item.body ? (
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{item.body}</p>
        ) : null}

        {item.meta?.length ? (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {item.meta.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}
